import url from 'url';

import $ from 'jquery';
import Cookies from 'js-cookie';
import _each from 'lodash/each';

import { showErrorBar } from 'actions/errorBar';
import {
  updateLoginEmailError,
  updateLoginPasswordError,
  updateLoginPlainError,
} from 'actions/login';
import { APP_SOURCE_WEBSITE } from 'constants/analytics';
import {
  APPLE,
  AUTH0,
  GOOGLE,
  NONE,
  SLACK,
  USERNAME,
  WINDOWS,
} from 'constants/authTypes';
import { KEY_ANON_COMMENT, KEY_ANON_COMMENT_NAME } from 'constants/cookie';
import { ERROR } from 'constants/error-severities';
import { EMAIL_LOGIN_BUTTON_CLICKED } from 'constants/events';
import { LO_OAUTH as LO_OAUTH_MESSAGE } from 'constants/messages';
import { JSON as JSON_MIME } from 'constants/mimes';
import { ANON_RECORD_A_REPLY } from 'constants/onboarding';
import {
  LOGIN,
  LOGIN_PAGE,
  LOGOUT,
  LOOM_BARE_URI,
  LOOM_URI,
  MY_VIDEOS_PAGE,
  WELCOME,
} from 'constants/routes';
import GetPrimaryAuthTypeForEmail from 'creators/GetPrimaryAuthTypeForEmail.graphql';
import { logInUserCreator } from 'creators/login';
import * as analytics from 'utilities/analytics';
import * as extension from 'utilities/extension';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/loggerx';
import { incrementMetric } from 'utilities/metrics';
import { store } from 'utilities/store';
import { getParam } from 'utilities/url';

import * as oAuthKeys from '../../../shared/constants/oAuthKeys';
import isUnsafeRedirectUrl from './isUnsafeRedirectUrl';
import { requestAcceptInviteLink } from './inviteLink';
import { requestAcceptWorkspaceInvite } from './workspace';

import { afterOauthLogin } from 'components/share-video/share-record-reply-signup-modal/utilities';

const afterOAuthComplete = async data => {
  let redirectURL;
  const cameFrom = getParam('redirect_after');
  const orgToken = getParam('orgToken');
  const inviteLinkId = getParam('invite_link_id');

  if (!data.loginSuccess) {
    redirectURL = LOGIN_PAGE;
  } else if (data.created) {
    redirectURL = WELCOME;
  } else if (cameFrom) {
    let cameFromDecoded = window.decodeURIComponent(cameFrom);

    if (isUnsafeRedirectUrl(cameFromDecoded)) {
      cameFromDecoded = '/';
    }

    redirectURL = cameFromDecoded;
  } else {
    redirectURL = MY_VIDEOS_PAGE;
  }

  if (data.error != null) {
    redirectURL += '?oauth_error=' + data.error;
  }

  if (orgToken) {
    try {
      await requestAcceptWorkspaceInvite(orgToken);
    } catch (err) {
      console.error('TeamInvite: Unable to accept invite after oauth');
    }
  } else if (inviteLinkId) {
    try {
      const { success, __typename: responseType } =
        await requestAcceptInviteLink(inviteLinkId, data.created);

      if (responseType === 'GenericError' || !success) {
        logger.warning(
          new Error(
            'InviteLink: Unable to process link after oauth authorization'
          ),
          {
            invite_link_id: inviteLinkId,
            accept_failure_reason: responseType,
          }
        );
      }
    } catch (err) {
      logger.warning(
        err`InviteLink: Unable to process link after oauth authorization`,
        {
          invite_link_id: inviteLinkId,
          error_message: err.message,
        }
      );
    }
  }

  if (data.loginSuccess) {
    extension.signalExtensionLogin(() => {
      window.location.href = redirectURL;
    });
  } else {
    window.location.href = redirectURL;
  }
};

export const login = (email, password) => {
  const { query } = url.parse(window.location.search, true);

  const loginUrl = {
    pathname: LOGIN,
    query: {
      app_source: APP_SOURCE_WEBSITE,
      ...query,
    },
  };

  const pathString = url.format(loginUrl);

  return $.ajax({
    method: 'POST',
    url: pathString,
    contentType: JSON_MIME,
    processData: false,
    data: JSON.stringify({ email, password }),
  });
};

export const logout = () => {
  return $.post(LOGOUT);
};

export const openOAuthPopup = (
  provider,
  extraParams = {},
  cb = afterOAuthComplete
) => {
  let href = `${LOOM_URI}/api/auth/${provider}`;

  const extraParamsString = Object.keys(extraParams).map(
    k => `${k}=${extraParams[k]}`
  );
  const app_source = getParam('app_source') || APP_SOURCE_WEBSITE;
  const params = [`app_source=${app_source}`, ...extraParamsString];

  analytics.ready(() => {
    const { anonID } = analytics.getAnalyticsIds();

    params.push(`anon_id=${anonID}`);

    if (analytics.amplitudeLoaded()) {
      params.push(
        `device_id=${window.amplitude.getInstance().options.deviceId}`
      );
    }

    href += `?${params[0]}`;

    _each(params.slice(1), param => {
      href += `&${param}`;
    });

    const oAuthPopup = window.open(href, 'loom_popup', 'width=600,height=800');

    $(window).on('message.loom-oauth', e => {
      const origin =
        e.origin || (e.originalEvent && e.originalEvent.origin) || '';

      let data;

      try {
        data = JSON.parse(
          e.data || (e.originalEvent && e.originalEvent.data) || '{}'
        );
      } catch (err) {
        return console.error(err, 'bad OAuth data passed');
      }

      if (origin.indexOf(LOOM_BARE_URI) < 0 || data.type !== LO_OAUTH_MESSAGE) {
        return;
      }

      $(window).off('message.loom-oauth');
      oAuthPopup?.close();

      cb(data);
    });
  });
};

export const getAnonUserName = () => {
  return Cookies.get(KEY_ANON_COMMENT_NAME) || '';
};

export const getAnonUserId = () => {
  return Cookies.get(KEY_ANON_COMMENT) || '';
};

export const signInHandler = async ({
  authType,
  email,
  password,
  expansionHandler = () => {},
  source = 'Sign in page',
  anonVideoProps = {},
}) => {
  try {
    switch (authType) {
      case AUTH0:
        if (source === ANON_RECORD_A_REPLY) {
          openOAuthPopup(
            oAuthKeys.AUTH0,
            {
              login_hint: email,
            },
            data => {
              if (data.loginSuccess) {
                afterOauthLogin({
                  ...anonVideoProps,
                  provider: authType,
                });
              } else {
                logger.error(new Error('Failed to log in via Auth0'), {
                  error: data.error,
                  source,
                });
                store.dispatch(showErrorBar(ERROR, data.error));
              }
            }
          );

          break;
        }

        openOAuthPopup(oAuthKeys.AUTH0, {
          login_hint: email,
        });
        break;

      case GOOGLE || SLACK || WINDOWS || APPLE:
        if (source === ANON_RECORD_A_REPLY) {
          openOAuthPopup(
            authType,
            {
              login_hint: email,
            },
            data => {
              if (data.loginSuccess) {
                afterOauthLogin({
                  ...anonVideoProps,
                  provider: authType,
                });
              } else {
                logger.error(new Error('Failed to log in via OAuth'), {
                  error: data.error,
                  source,
                });
                store.dispatch(showErrorBar(ERROR, data.error));
              }
            }
          );

          break;
        }

        openOAuthPopup(authType, {
          login_hint: email,
        });
        break;

      case USERNAME:
        store.dispatch(logInUserCreator(email, password, window.location.href));
        break;

      case NONE:
        await expansionHandler();
        break;

      default:
        logger.error(new Error('Unexpected auth type'), { authType, email });
        store.dispatch(
          showErrorBar(
            ERROR,
            'Unable to log this user in at this time. Try refreshing your browser.'
          )
        );
        break;
    }
  } catch (err) {
    store.dispatch(
      showErrorBar(ERROR, 'Unable to log this user in at this time.')
    );
  }

  // Do not emit login analytics if action is account creation
  if (authType !== NONE) {
    analytics.track(EMAIL_LOGIN_BUTTON_CLICKED, { source });

    incrementMetric('button.click', {
      context: EMAIL_LOGIN_BUTTON_CLICKED,
      source,
    });
  }
};

export const clearLoginErrors = () => {
  store.dispatch(updateLoginEmailError(null));
  store.dispatch(updateLoginPasswordError(null));
  store.dispatch(updateLoginPlainError(null));
};

export const getPrimaryAuthTypeForEmail = email => {
  const client = getGraphQLClient();

  return client.query({
    query: GetPrimaryAuthTypeForEmail,
    variables: { email },
    fetchPolicy: 'no-cache',
  });
};
